import { useEffect, type ReactNode } from 'react';
import { HeaderShell, ThemeButton } from './components/Header.tsx';
import { ChevronIcon, Ornament, QuestionIcon } from './components/icons.tsx';
import { useAppContext } from './hooks/useAppContext.ts';
import { useTheme } from './hooks/useTheme.ts';

/*
 * The home page: the two games, and the chat when it is switched on (CHAT_ENABLED in the
 * API, read from /api/context). Each one lives at its own address and a link reloads.
 */

const arrow = (
  <span className="transition-transform duration-150 group-hover:translate-x-0.5">
    <ChevronIcon />
  </span>
);

function GameCard({
  href,
  kicker,
  title,
  cta,
  featured = false,
  children,
}: {
  href: string;
  kicker: string;
  title: string;
  cta: string;
  featured?: boolean;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      className={
        'group block rounded-2xl px-5 py-5 no-underline transition duration-150 active:scale-[0.99] ' +
        (featured
          ? 'border-2 border-gold bg-purple text-cream hover:bg-purple-500 dark:bg-night-700 dark:hover:bg-night-800'
          : 'border-2 border-gold/60 bg-paper text-ink hover:border-gold hover:bg-white dark:border-gold/35 dark:bg-night-800 dark:text-cream dark:hover:border-gold/70')
      }
    >
      <span
        className={`block text-xs font-semibold tracking-[0.16em] uppercase ${featured ? 'text-gold-300' : 'text-gold-700 dark:text-gold-300'}`}
      >
        {kicker}
      </span>
      <span
        className={`mt-1.5 block font-serif text-[2rem] leading-[1.05] font-semibold ${featured ? '' : 'text-purple dark:text-cream'}`}
      >
        {title}
      </span>
      <span
        className={`mt-2 block text-[1.05rem] leading-snug ${featured ? 'text-cream/85' : 'text-ink-soft dark:text-cream/80'}`}
      >
        {children}
      </span>
      <span
        className={`mt-3 inline-flex items-center gap-1 font-semibold ${featured ? 'text-gold-300' : 'text-purple dark:text-gold-300'}`}
      >
        {cta}
        {arrow}
      </span>
    </a>
  );
}

export function Home() {
  const ctx = useAppContext();
  const theme = useTheme(ctx?.now ?? null);
  useEffect(() => {
    document.title = 'Cruz de IA · Juegos cofrades';
  }, []);

  return (
    <div className="flex min-h-dvh flex-col">
      <HeaderShell title="Cruz de IA" subtitle="Semana Santa de Sevilla">
        <ThemeButton theme={theme.mode} onTheme={theme.cycle} />
      </HeaderShell>
      <main className="animate-rise mx-auto w-full max-w-2xl flex-1 space-y-4 px-4 py-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
        <div className="px-1 pt-1">
          <h1 className="font-serif text-[2.4rem] leading-[1.05] font-semibold text-purple dark:text-cream">
            La Semana Santa, todo el año
          </h1>
          <p className="mt-2 text-lg leading-snug text-ink-soft dark:text-cream/75">
            Juegos cofrades para que la espera se haga más corta, con los tuyos.
          </p>
          <Ornament className="mt-3" />
        </div>

        <GameCard
          href="/simulador"
          kicker="Un rato cada día"
          title="De cirio a hermano mayor"
          cta="Empezar mi carrera"
          featured
        >
          Funda tu hermandad e inscríbete como el último hermano. Empiezas de cirio, en el primer
          tramo… y quién sabe dónde acabas.
        </GameCard>
        <a
          href="/simulador/tramo"
          className="group -mt-1 flex items-center gap-3 rounded-xl px-2 py-1.5 text-ink no-underline dark:text-cream"
        >
          <span className="min-w-0 flex-1 text-[1.02rem] leading-snug">
            <strong className="font-semibold text-purple dark:text-gold-300">
              El tramo del día:
            </strong>{' '}
            medio minuto, sin registrarte. El mismo para todo el mundo.
          </span>
          <span className="text-gold-700 dark:text-gold">{arrow}</span>
        </a>

        <GameCard
          href="/reto/nuevo"
          kicker="Para el grupo de amigos"
          title="Reto cofrade"
          cta="Crear un reto"
        >
          ¿Cuánto sabes de la Semana Santa de Sevilla? Crea un reto de preguntas, mándalo por
          WhatsApp y a ver quién sabe más.
        </GameCard>

        <GameCard
          href="/juegos/revira"
          kicker="Partidas de un minuto"
          title="La revirá"
          cta="Sacar el paso"
        >
          Lleva el paso por las calles del barrio: toca para girar en cada esquina y que no roce
          nada.
        </GameCard>

        {ctx?.chat && (
          <a
            href="/pregunta"
            className="group flex items-center gap-3 rounded-xl border border-purple/15 bg-paper px-4 py-3 text-ink no-underline transition duration-150 hover:border-purple/35 hover:bg-white active:scale-[0.99] dark:border-cream/12 dark:bg-night-800 dark:text-cream dark:hover:border-gold/50"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-purple/[0.07] text-purple dark:bg-gold/10 dark:text-gold-300">
              <QuestionIcon />
            </span>
            <span className="min-w-0 flex-1">
              <strong className="block font-serif text-xl leading-tight font-semibold">
                Pregúntale a Cruz de IA
              </strong>
              <span className="block text-sm leading-snug text-ink-soft dark:text-cream/70">
                Horarios, recorridos y hermandades.
              </span>
            </span>
            <span className="text-gold-700 dark:text-gold">{arrow}</span>
          </a>
        )}
      </main>
    </div>
  );
}
